import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faSpinner, 
  faSave, 
  faTrash, 
  faPlus,
  faTimes,
  faToggleOn,
  faToggleOff,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import { WorkflowService, sharedApiClient } from '../fetcher';
import { IdentityService, TeamService, useNotifications, useAuth, CustomDropdown, CustomDatePicker, BROWSER_PAGE_LABEL, medPromptBrowserTitle, todayCalendarDate, calendarDateDaysAgo } from '@medprompt/shared';
import { UserAbility } from '@medprompt/shared';
import { Table, createTextColumn, createEnumColumn, createCustomColumn, useTableState } from '@medprompt/shared';
import { StatusBadge } from '@medprompt/shared';
import WorkflowDiagram from './components/WorkflowDiagram';
import WorkflowConditionCard from './components/WorkflowConditionCard';
import { DEFAULT_NEW_CONDITION, getWorkflowConditionsValidationError } from './WorkflowConditionEnum';
import WorkflowActionCard from './components/WorkflowActionCard';
import { DEFAULT_NEW_ACTION } from './WorkflowActionEnum';
import './WorkflowView.css';
import '@medprompt/shared/src/common/modals.css';

// Delete confirmation modal
const DeleteWorkflowModal = ({ isOpen, onClose, onConfirm, workflow, loading }) => {
  if (!isOpen || !workflow) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-animated">
        <div className="modal-header">
          <h2>Delete Workflow</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="modal-content">
          <p style={{ color: '#d32f2f', fontWeight: 'bold', marginBottom: '15px' }}>
            Are you sure you want to delete this workflow? This action cannot be undone.
          </p>
          <div className="detail-row" style={{ marginBottom: '8px' }}>
            <span className="detail-label" style={{ fontWeight: 600 }}>Workflow Name:</span>
            <span className="detail-value">{workflow.name || 'Unnamed Workflow'}</span>
          </div>
          <div className="detail-row" style={{ marginBottom: '8px' }}>
            <span className="detail-label" style={{ fontWeight: 600 }}>Conditions:</span>
            <span className="detail-value">{workflow.conditions?.length || 0}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label" style={{ fontWeight: 600 }}>Actions:</span>
            <span className="detail-value">{workflow.actions?.length || 0}</span>
          </div>
        </div>
        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="header-button header-button-delete"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Delete Workflow'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Workflow Logs modal - self-contained with its own data fetching
const WorkflowLogsModal = ({ isOpen, onClose, ruleId }) => {
  console.log('[WorkflowLogsModal] RENDER - isOpen:', isOpen, 'ruleId:', ruleId);
  
  const navigate = useNavigate();
  const workflowManager = useMemo(() => new WorkflowService(sharedApiClient), []);

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logLevel, setLogLevel] = useState('ALL');
  const [logLevelDropdownOpen, setLogLevelDropdownOpen] = useState(false);
  const [dateRangeSelection, setDateRangeSelection] = useState({ value: 'last7days', display: 'Last 7 days' });
  const [dateRangeDropdownOpen, setDateRangeDropdownOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    from: calendarDateDaysAgo(7),
    to: todayCalendarDate()
  });

  const {
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState
  } = useTableState('WorkflowLogsTable', {
    defaultSortState: { columnKey: 'timestamp', direction: 'desc' }
  });

  // Fetch logs when modal opens or filters change
  useEffect(() => {
    console.log('[WorkflowLogsModal] useEffect triggered - isOpen:', isOpen, 'ruleId:', ruleId);
    
    if (!isOpen || !ruleId) {
      console.log('[WorkflowLogsModal] useEffect SKIPPED - isOpen:', isOpen, 'ruleId:', ruleId);
      return;
    }

    console.log('[WorkflowLogsModal] useEffect EXECUTING - about to fetch logs');

    const fetchLogs = async () => {
      try {
        console.log('[WorkflowLogsModal] fetchLogs START');
        setLoading(true);
        
        // Calculate date range
        const now = new Date();
        let from;
        
        if (dateRangeSelection.value === 'custom') {
          from = new Date(customDateRange.from);
        } else {
          switch (dateRangeSelection.value) {
            case 'last24h':
              from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
              break;
            case 'last7days':
              from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              break;
            case 'lastMonth':
              from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              break;
            default:
              from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          }
        }

        const to = dateRangeSelection.value === 'custom' ? new Date(customDateRange.to + 'T23:59:59.999Z') : now;
        
        // CRITICAL: Send as epoch millis NUMBERS, not ISO strings
        const fromEpochMillis = from.getTime();
        const toEpochMillis = to.getTime();
        
        console.log('[WorkflowLogsModal] ABOUT TO CALL API - ruleId:', ruleId);
        console.log('[WorkflowLogsModal] from (epoch millis):', fromEpochMillis, 'to (epoch millis):', toEpochMillis);
        console.log('[WorkflowLogsModal] from (date):', new Date(fromEpochMillis), 'to (date):', new Date(toEpochMillis));
        console.log('[WorkflowLogsModal] workflowManager:', workflowManager);
        
        const data = await workflowManager.fetchWorkflowLogs(ruleId, fromEpochMillis, toEpochMillis);
        
        console.log('[WorkflowLogsModal] API RESPONSE RECEIVED:', data);
        setLogs(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('[WorkflowLogsModal] API ERROR:', error);
        setLogs([]);
      } finally {
        console.log('[WorkflowLogsModal] fetchLogs COMPLETE');
        setLoading(false);
      }
    };

    fetchLogs();
  }, [isOpen, ruleId, dateRangeSelection.value, customDateRange.from, customDateRange.to, workflowManager]);

  const handleDateRangeSelect = useCallback((option) => {
    setDateRangeSelection(option);
    setDateRangeDropdownOpen(false);
  }, []);

  const handleCustomDateChange = useCallback((field, value) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const columns = useMemo(() => [
    createCustomColumn(
      'timestamp',
      'Timestamp',
      (log) => log.timestamp,
      (value) => {
        if (!value) return '—';
        try {
          return new Date(value).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          });
        } catch {
          return value;
        }
      },
      {
        sortable: true,
        filterable: false,
        compareFn: (a, b) => {
          const va = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const vb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return va - vb;
        }
      }
    ),

    createTextColumn('message', 'Message', (log) => log.message),

    createEnumColumn(
      'logLevel',
      'Level',
      (log) => log.logLevel,
      ['INFO', 'ERROR'],
      {
        renderer: (value) => (
          <StatusBadge
            label={value}
            colorFamily={value === 'ERROR' ? 'red' : 'teal'}
            size="normal"
          />
        ),
        sortable: true
      }
    )
  ], []);

  const filteredLogs = useMemo(() => {
    if (logLevel === 'ALL') return logs;
    return logs.filter(log => log.logLevel === logLevel);
  }, [logs, logLevel]);

  const handleLogClick = (log) => {
    if (log.documentId) {
      navigate(`/document-detail/${log.documentId}`, {
        state: {
          fromWorkflowId: ruleId
          // Don't pass openWorkflowLogs here - we only want to reopen modals when coming BACK
        }
      });
    }
  };

  const selectedLogLevel = LOG_LEVEL_OPTIONS.find(opt => opt.value === logLevel) || LOG_LEVEL_OPTIONS[0];

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-animated" style={{ width: '90%', maxWidth: '1400px' }}>
        <div className="modal-header">
          <h2>Workflow Logs</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="modal-content" style={{ padding: '20px' }}>
          <div className="logs-filters" style={{ marginBottom: '20px' }}>
            {dateRangeSelection.value === 'custom' && (
              <div className="custom-date-inputs">
                <CustomDatePicker
                  value={customDateRange.from}
                  onChange={(value) => handleCustomDateChange('from', value)}
                  label="From"
                  max={customDateRange.to}
                />
                <CustomDatePicker
                  value={customDateRange.to}
                  onChange={(value) => handleCustomDateChange('to', value)}
                  label="To"
                  min={customDateRange.from}
                  max={todayCalendarDate()}
                />
              </div>
            )}
            <CustomDropdown
              options={DATE_RANGE_OPTIONS}
              selectedOption={dateRangeSelection.display}
              onSelect={handleDateRangeSelect}
              isOpen={dateRangeDropdownOpen}
              toggleDropdown={() => setDateRangeDropdownOpen(!dateRangeDropdownOpen)}
              keyExtractor={(option, index) => `date-range-${option.value || index}`}
            />
            <CustomDropdown
              label="Level"
              selected={selectedLogLevel.name}
              options={LOG_LEVEL_OPTIONS}
              isOpen={logLevelDropdownOpen}
              toggleDropdown={() => setLogLevelDropdownOpen(!logLevelDropdownOpen)}
              onSelect={(option) => {
                setLogLevel(option.value);
                setLogLevelDropdownOpen(false);
              }}
              keyExtractor={(option) => `log-level-${option.value}`}
              width="150px"
            />
          </div>
          {loading ? (
            <div className="logs-loading" style={{ textAlign: 'center', padding: '40px' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>Loading logs...</p>
            </div>
          ) : (
            <div className="logs-table-container" style={{ minHeight: '400px' }}>
              <Table
                id="WorkflowLogsTable"
                data={filteredLogs}
                columns={columns}
                getRowKey={(log, index) => `${log.timestamp}-${log.documentId}-${index}`}
                sortState={sortState}
                onSortChange={setSortState}
                filterValues={filterValues}
                onFilterChange={setFilterValues}
                disabledFilters={disabledFilters}
                onDisabledFiltersChange={setDisabledFilters}
                paginationState={paginationState}
                onPaginationChange={setPaginationState}
                onRowClick={handleLogClick}
                emptyMessage="No logs found for this workflow in the selected date range."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Event type options
const EVENT_TYPE_OPTIONS = [
  { value: 'DOCUMENT_COMPLETED', name: 'Document Completed' }
];

// Date range options
const DATE_RANGE_OPTIONS = [
  { value: 'last24h', display: 'Last 24h' },
  { value: 'last7days', display: 'Last 7 days' },
  { value: 'lastMonth', display: 'Last month' },
  { value: 'custom', display: 'Custom' }
];

// Log level options
const LOG_LEVEL_OPTIONS = [
  { value: 'ALL', name: 'All Levels' },
  { value: 'INFO', name: 'Info' },
  { value: 'ERROR', name: 'Error' }
];

// Session storage key for tracking if workflow was opened from a document
const WORKFLOW_FROM_DOCUMENT_KEY = 'workflowFromDocument';

const WorkflowDetailView = () => {
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  const hasConfigWrite = user?.authorities?.includes(UserAbility.CONFIG_WRITE.key);

  const workflowManager = useMemo(() => new WorkflowService(sharedApiClient), []);

  // Workflow state
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Form state
  const [workflowName, setWorkflowName] = useState('');
  const [workflowEvent, setWorkflowEvent] = useState('DOCUMENT_COMPLETED');
  const [workflowActive, setWorkflowActive] = useState(true);
  const [conditions, setConditions] = useState([]);
  const [actions, setActions] = useState([]);

  // Modal state
  const [logsModalOpen, setLogsModalOpen] = useState(false);

  // Track if we came from a document
  const [fromDocumentId, setFromDocumentId] = useState(null);

  // Users and teams
  const [activeUsers, setActiveUsers] = useState([]);
  const [teams, setTeams] = useState([]);

  // Event dropdown state
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);

  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const titleInputRef = useRef(null);

  // Session storage key for auto-save
  const getStorageKey = () => `medprompt_workflow_edit_${ruleId}`;

  useEffect(() => {
    document.title = medPromptBrowserTitle('Configure Document Workflow');
  }, []);

  // Read/recover document context from location state or session storage on mount
  useEffect(() => {
    try {
      // Check location.state first (fresh navigation)
      const documentIdFromState = location.state?.documentId;

      if (documentIdFromState) {
        // Fresh navigation from document - persist to session storage
        console.log('Workflow opened from document:', documentIdFromState);
        setFromDocumentId(documentIdFromState);
        sessionStorage.setItem(WORKFLOW_FROM_DOCUMENT_KEY, documentIdFromState);
      } else {
        // Check session storage for refresh recovery
        const documentIdFromStorage = sessionStorage.getItem(WORKFLOW_FROM_DOCUMENT_KEY);
        if (documentIdFromStorage) {
          console.log('Recovered document context from session storage:', documentIdFromStorage);
          setFromDocumentId(documentIdFromStorage);
        } else {
          setFromDocumentId(null);
        }
      }
    } catch (error) {
      console.warn('Failed to read/recover document context:', error);
      setFromDocumentId(null);
    }
  }, [location.state, ruleId]);

  // Handle reopening logs modal when returning from a document
  useEffect(() => {
    if (location.state?.openWorkflowLogs) {
      console.log('Reopening workflow logs modal');
      setLogsModalOpen(true);
    }
  }, [location.state]);

  // Fetch users and teams
  useEffect(() => {
    const identityManager = new IdentityService();
    const teamManager = new TeamService();
    
    identityManager.fetchActiveClinicUsers()
      .then((users) => setActiveUsers(users || []))
      .catch((error) => {
        console.error('Failed to fetch active users:', error);
        setActiveUsers([]);
      });
    
    teamManager.fetchTeams()
      .then((teamsData) => setTeams(teamsData || []))
      .catch((error) => {
        console.error('Failed to fetch teams:', error);
        setTeams([]);
      });
  }, []);

  // Fetch workflow
  useEffect(() => {
    if (!ruleId) return;

    const fetchWorkflow = async () => {
      try {
        setLoading(true);
        const data = await workflowManager.fetchWorkflowRule(ruleId);
        setWorkflow(data);
        setWorkflowName(data.name || '');
        setWorkflowEvent(data.event || 'DOCUMENT_COMPLETED');
        setWorkflowActive(data.isActive);
        setConditions(data.conditions || []);
        setActions(data.actions || []);
      } catch (error) {
        console.error('Failed to fetch workflow:', error);
        addNotification('fetch-workflow-error', 'Failed to fetch workflow', { isError: true });
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflow();
  }, [ruleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load draft from session storage after initial workflow fetch
  useEffect(() => {
    if (!workflow || loading) return;

    const storageKey = getStorageKey();
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.name !== undefined) setWorkflowName(draft.name);
        if (draft.isActive !== undefined) setWorkflowActive(draft.isActive);
        if (draft.conditions) setConditions(draft.conditions);
        if (draft.actions) setActions(draft.actions);
      }
    } catch (error) {
      console.error('Failed to load draft from session storage:', error);
    }
  }, [workflow, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save draft to session storage whenever form state changes
  useEffect(() => {
    if (!workflow || loading) return;

    const storageKey = getStorageKey();
    try {
      const draft = {
        name: workflowName,
        isActive: workflowActive,
        conditions: conditions,
        actions: actions
      };
      sessionStorage.setItem(storageKey, JSON.stringify(draft));
    } catch (error) {
      console.error('Failed to save draft to session storage:', error);
    }
  }, [workflowName, workflowActive, conditions, actions, workflow, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isEditingTitle]);

  // Title editing handlers
  const handleTitleClick = () => {
    if (hasConfigWrite) {
      setIsEditingTitle(true);
    }
  };

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setWorkflowName(workflow?.name || '');
      setIsEditingTitle(false);
    } else if (e.key === 'Enter') {
      setIsEditingTitle(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    // If we came from a document, navigate back to it with modals open
    if (fromDocumentId) {
      try {
        sessionStorage.removeItem(WORKFLOW_FROM_DOCUMENT_KEY);
      } catch (error) {
        console.warn('Failed to clear document context from session storage:', error);
      }

      navigate(`/document-detail/${fromDocumentId}`, {
        state: {
          openWorkflowLogs: true,
          previewRuleId: ruleId
        }
      });
    } else {
      // Default: navigate back to workflows list
      sessionStorage.setItem('lastActivePage', 'workflows');
      navigate('/workflows');
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!hasConfigWrite) {
      addNotification('save-permission-error', 'You do not have permission to save workflows', { isError: true });
      return;
    }

    if (!workflowName.trim()) {
      addNotification('save-validation-error', 'Workflow name is required', { isError: true });
      return;
    }

    if (actions.length === 0) {
      addNotification('save-validation-error', 'At least one action is required', { isError: true });
      return;
    }

    const conditionsError = getWorkflowConditionsValidationError(conditions);
    if (conditionsError) {
      addNotification('save-validation-error', conditionsError, { isError: true });
      return;
    }

    try {
      setSaving(true);

      const update = {
        ruleId: ruleId,
        name: workflowName.trim(),
        event: workflowEvent,
        conditions: conditions,
        actions: actions,
        activate: workflowActive,
        deactivate: !workflowActive
      };
      
      await workflowManager.updateWorkflowRule(update);
      addNotification('workflow-updated', 'Workflow updated successfully');
      
      // Clear draft from session storage after successful save
      const storageKey = getStorageKey();
      try {
        sessionStorage.removeItem(storageKey);
      } catch (error) {
        console.error('Failed to clear draft from session storage:', error);
      }
    } catch (error) {
      console.error('Failed to save workflow:', error);
      addNotification('save-workflow-error', 'Failed to save workflow', { isError: true });
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!workflow) return;
    
    setDeleteLoading(true);
    try {
      await workflowManager.deleteWorkflowRule(workflow.ruleId);
      addNotification('workflow-deleted', 'Workflow deleted successfully');
      setDeleteModalOpen(false);
      sessionStorage.setItem('lastActivePage', 'workflows');
      navigate('/workflows');
    } catch (error) {
      console.error('Failed to delete workflow:', error);
      addNotification('delete-workflow-error', 'Failed to delete workflow', { isError: true });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Condition handlers
  const handleAddCondition = () => {
    setConditions([...conditions, { ...DEFAULT_NEW_CONDITION }]);
  };

  const handleUpdateCondition = (index, newCondition) => {
    const newConditions = [...conditions];
    newConditions[index] = newCondition;
    setConditions(newConditions);
  };

  const handleRemoveCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  // Action handlers
  const handleAddAction = () => {
    setActions([...actions, { ...DEFAULT_NEW_ACTION }]);
  };

  const handleUpdateAction = (index, newAction) => {
    const newActions = [...actions];
    newActions[index] = newAction;
    setActions(newActions);
  };

  const handleRemoveAction = (index) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  // Current workflow for diagram
  const currentWorkflow = useMemo(() => ({
    name: workflowName,
    event: workflowEvent,
    conditions: conditions,
    actions: actions,
    isActive: workflowActive
  }), [workflowName, workflowEvent, conditions, actions, workflowActive]);

  const selectedEventType = EVENT_TYPE_OPTIONS.find(opt => opt.value === workflowEvent) || EVENT_TYPE_OPTIONS[0];

  if (loading) {
    return (
      <div className="workflow-detail-view">
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          <p>Loading workflow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workflow-detail-view">
      {/* Header */}
      <div className="workflow-detail-header">
        <div className="header-left">
          <button className="header-button" onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> {fromDocumentId ? 'Back to Document' : 'Back to Workflows'}
          </button>
        </div>
        <div className="workflow-actions">
          <button
            className="header-button"
            onClick={() => {
              console.log('[WorkflowDetailView] View Logs button CLICKED - current logsModalOpen:', logsModalOpen, 'ruleId:', ruleId);
              setLogsModalOpen(true);
              console.log('[WorkflowDetailView] setLogsModalOpen(true) CALLED');
            }}
          >
            <FontAwesomeIcon icon={faClipboardList} /> View Logs
          </button>
          <button
            className="header-button header-button-delete"
            onClick={handleDeleteClick}
            disabled={!hasConfigWrite}
          >
            <FontAwesomeIcon icon={faTrash} /> Delete
          </button>
          <button
            className="header-button header-button-primary"
            onClick={handleSave}
            disabled={!hasConfigWrite || saving}
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} /> Save
              </>
            )}
          </button>
        </div>
      </div>

      {/* Two-panel layout */}
      <div className="workflow-detail-content workflow-two-panel">
        {/* Left Panel - Diagram (2/3 width) */}
        <div className="workflow-panel workflow-panel-diagram">
          <div className="panel-header">
            <h2>Workflow Visualization</h2>
          </div>
          <div className="diagram-content">
            <WorkflowDiagram workflow={currentWorkflow} activeUsers={activeUsers} teams={teams} />
          </div>
        </div>

        {/* Right Panel - Builder (1/3 width) */}
        <div className="workflow-panel workflow-panel-config">
          <div className="panel-header">
            <h2>Configure Document Workflow</h2>
          </div>

          <div className="workflow-config-content-wrapper">
            <div className="workflow-config-section">
            <div className="config-row workflow-title-section">
              <label>Workflow Name:</label>
              {isEditingTitle && hasConfigWrite ? (
                <input
                  ref={titleInputRef}
                  type="text"
                  className="workflow-title-input"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  onBlur={handleTitleBlur}
                  onKeyDown={handleTitleKeyDown}
                  placeholder="Add title here"
                />
              ) : (
                <div
                  className={`workflow-title-text ${hasConfigWrite ? 'workflow-title-editable' : ''}`}
                  onClick={handleTitleClick}
                  title={hasConfigWrite ? "Click to edit" : ""}
                >
                  {workflowName || <em style={{ color: '#999' }}>Add title here</em>}
                </div>
              )}
            </div>

            <div className="config-row">
              <label>Status:</label>
              <button
                className={`status-toggle ${workflowActive ? 'active' : 'inactive'}`}
                onClick={() => setWorkflowActive(!workflowActive)}
                disabled={!hasConfigWrite}
                type="button"
              >
                <FontAwesomeIcon icon={workflowActive ? faToggleOn : faToggleOff} />
                {workflowActive ? 'Active' : 'Inactive'}
              </button>
            </div>

            <div className="config-row">
              <label>Event Type:</label>
              <span style={{ 
                fontFamily: 'Quicksand, sans-serif', 
                color: '#333',
                fontSize: '15px'
              }}>
                When a document completes processing
              </span>
            </div>
          </div>

          {/* Conditions Section */}
          <div className="workflow-builder-section">
            <div className="section-header">
              <h3>Conditions</h3>
              <button
                className="add-button"
                onClick={handleAddCondition}
                disabled={!hasConfigWrite}
                type="button"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Condition
              </button>
            </div>
            <div className="cards-container">
              {conditions.length === 0 ? (
                <div className="empty-state">
                  <p>No conditions defined. Add a condition to filter when this workflow runs.</p>
                </div>
              ) : (
                conditions.map((condition, index) => (
                  <WorkflowConditionCard
                    key={index}
                    condition={condition}
                    index={index}
                    onChange={(newCondition) => handleUpdateCondition(index, newCondition)}
                    onRemove={handleRemoveCondition}
                    activeUsers={activeUsers}
                    teams={teams}
                  />
                ))
              )}
            </div>
          </div>

          {/* Actions Section */}
          <div className="workflow-builder-section">
            <div className="section-header">
              <h3>Actions</h3>
              <button
                className="add-button"
                onClick={handleAddAction}
                disabled={!hasConfigWrite}
                type="button"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Action
              </button>
            </div>
            <div className="cards-container">
              {actions.length === 0 ? (
                <div className="empty-state">
                  <p>No actions defined. Add at least one action to execute when conditions are met.</p>
                </div>
              ) : (
                actions.map((action, index) => (
                  <WorkflowActionCard
                    key={index}
                    action={action}
                    index={index}
                    onChange={(newAction) => handleUpdateAction(index, newAction)}
                    onRemove={handleRemoveAction}
                    activeUsers={activeUsers}
                    teams={teams}
                  />
                ))
              )}
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <DeleteWorkflowModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        workflow={workflow}
        loading={deleteLoading}
      />

      {/* Workflow logs modal */}
      <WorkflowLogsModal
        isOpen={logsModalOpen}
        onClose={() => {
          console.log('[WorkflowDetailView] Closing logs modal');
          setLogsModalOpen(false);
        }}
        ruleId={ruleId}
      />
    </div>
  );
};

export default WorkflowDetailView;
